import mt from 'moment-timezone';
import React from 'react';
import ReactHtmlParser from 'react-html-parser';
import { getSelectedText, roundFloat } from 'utils/helpers';
import { trackCustomEvent } from 'utils/analytics';
import ResponsiveImage from 'components/elements/common/ResponsiveImage/ResponsiveImage';

const crops = {
  desktop: '814x-1',
  tablet: '300x-1',
  mobile: '300x-1',
};

const jumpTo = (id) => {
  const to = document.getElementById(id.replace('-ref', ''));
  to.scrollIntoView();
};

const transformFootnote = ({ attribs }) => <sup key={attribs.id} onClick={() => jumpTo(attribs.id)} />;

const transformLink = ({ attribs, children }, index, history) => {
  const textNodes = [];
  children.forEach(({ type, data, name, children: tagChildren }) => {
    if (type === 'text') {
      textNodes.push(data);
    } else if (type === 'tag' && name !== 'meta' && tagChildren.length && tagChildren[0].data) {
      // accounts for times when the inner text is wrapped in a styled tag - ex. <em>Game of Thrones</em>
      textNodes.push(React.createElement(name, { key: `${tagChildren[0].data}_${index}` }, tagChildren[0].data));
    }
  });
  const revision = children.find(({ type, name, attribs: { itemprop } = {} }) => type === 'tag' && name === 'meta' && itemprop === 'suid');
  const optionalProps = {};

  if (revision) {
    optionalProps.onClick = (e) => {
      e.preventDefault();
      history.push(`/news/${revision.attribs.content}`);
    };
  }
  return <a key={`${attribs.href}_${index}`} href={attribs.href} target="_blank" rel="noopener noreferrer" {...optionalProps}>{textNodes}</a>;
};

const transformImage = ({ attribs }, index, images) => {
  const srcId = attribs['data-id'];
  const image = srcId && images.find(({ id }) => id === srcId);
  if (!image) {
    return null;
  }
  return <ResponsiveImage key={`article_${srcId}_${index}`} imageUrl={image.watermark} crops={crops} title={image.title} caption={image.description}/>;
};

const filterDivs = ({ attribs }) => {
  if (attribs && attribs.class && attribs.class.includes('news-designed-for-consumer-media')) {
    return null;
  }
};

const transform = (node, index, images, history) => {
  if (node.type === 'tag') {
    switch (node.name) {
      case 'figure':
        return transformImage(node, index, images);
      case 'a':
        return transformLink(node, index, history);
      case 'sup':
        return transformFootnote(node);
      case 'div':
        return filterDivs(node);
      case 'span':
        return filterDivs(node); // filter out news-designed-for-consumer-media which comes in spans sometimes
      case 'meta':
        return null;
      default:
        break;
    }
  }
};

const transformAttribution = (node, index) => {
  if (node.type === 'tag' && node.name === 'span' && node.attribs && node.attribs.class && node.attribs.class === 'news-dateline') {
    const attribution = node.children.find(child => child.type === 'text');
    if (attribution && attribution.data) {
      return <span className="article--attribution" key={index}>{attribution.data}</span>;
    }
  }
  return null;
};

const trackCopy = (wordCount) => {
  const selectedText = getSelectedText() || '';
  if (!selectedText.length) {
    return;
  }

  const numWords = selectedText.split(' ').filter(word => !!word).length;
  const percentage = roundFloat(numWords / wordCount * 100, 2);

  trackCustomEvent({
    category: 'news',
    action: 'copy',
  }, {
    numWords,
    percentage,
  });
};

const Body = ({
  header: { html: headerRaw = '' } = {},
  body: { html: bodyRaw = '' } = {},
  trashline: trashlineRaw,
  footer: { html: footerRaw = '' } = {},
  images,
  wordCount,
  history,
}) => {
  const body = ReactHtmlParser(bodyRaw, {
    transform: (node, index) => transform(node, index, images, history),
  });

  const trashline = ReactHtmlParser(trashlineRaw && trashlineRaw.html || '', {
    transform: (node, index) => transform(node, index, images, history),
  });

  const footer = ReactHtmlParser(footerRaw, {
    transform: (node, index) => transform(node, index, images, history),
  });

  const attribution = ReactHtmlParser(headerRaw, {
    transform: (node, index) => transformAttribution(node, index, images),
  });

  return (
    <article onCopy={() => trackCopy(wordCount)}>
      {attribution}
      {body}
      {trashline && <div className="article--trashline">{trashline}</div>}
      {footer}
      <div className="article--copyright">&copy; {mt().format('YYYY')} Bloomberg L.P.</div>
    </article>
  );
};

export default Body;
